import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Lock, MessageSquareCode, Play } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScoreBar } from "@/components/Primitives";

export default function Interview() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [session, setSession] = useState(null);
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState("");
  const [turns, setTurns] = useState([]);
  const [evaluation, setEvaluation] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);

  const access = user?.access;
  const noAccess = locked || (access && access.effectivePlan !== "career_plus");

  const loadHistory = () => api.get("/interviews").then(({ data }) => setHistory(data.interviews)).catch(() => setHistory([]));
  useEffect(() => {
    loadHistory();
  }, []);

  const start = async () => {
    setBusy("start");
    setError("");
    setEvaluation(null);
    setTurns([]);
    try {
      const { data } = await api.post("/interviews/start");
      setSession(data.interviewId);
      setQuestion(data.question);
    } catch (e) {
      if (e?.response?.status === 402) setLocked(true);
      setError(errMsg(e, "Unable to start interview"));
    } finally {
      setBusy("");
    }
  };

  const submitAnswer = async () => {
    setBusy("answer");
    setError("");
    try {
      const { data } = await api.post(`/interviews/${session}/answer`, { answer });
      setTurns([...turns, { question: question.question, category: question.category, answer, score: data.score, feedback: data.feedback }]);
      setAnswer("");
      if (data.done) {
        setQuestion(null);
        await finish();
      } else {
        setQuestion(data.question);
      }
    } catch (e) {
      setError(errMsg(e, "Unable to submit answer"));
    } finally {
      setBusy("");
    }
  };

  const finish = async () => {
    setBusy("finish");
    try {
      const { data } = await api.post(`/interviews/${session}/finish`);
      setEvaluation(data);
      setQuestion(null);
      loadHistory();
    } catch (e) {
      setError(errMsg(e, "Unable to finalise evaluation"));
    } finally {
      setBusy("");
    }
  };

  if (noAccess)
    return (
      <div className="border border-border p-10 text-center" data-testid="interview-locked">
        <Lock className="h-5 w-5 mx-auto text-primary" />
        <p className="mt-4 font-display text-lg font-bold">Mock interviews are a Career Plus feature</p>
        <p className="mt-2 text-sm text-muted-foreground">
          {error || (access?.trialExpired
            ? "Your free trial has ended. Upgrade to run AI-led interviews with scored evaluations."
            : "Upgrade to Career Plus to run AI-led interviews with scored evaluations.")}
        </p>
        <Link to="/pricing" className="inline-block mt-6" data-testid="interview-locked-cta"><Button className="rounded-none">View plans</Button></Link>
      </div>
    );

  return (
    <div className="space-y-10 fade-up" data-testid="interview-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="overline text-primary">mock interview</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-display font-black tracking-tighter">AI-led interview session</h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
            Five questions picked from your weakest categories and target role, with follow-ups when your answer
            leaves signal on the table. Ends with a scored evaluation and a study plan.
          </p>
        </div>
        {!question && (
          <Button className="rounded-none gap-2" onClick={start} disabled={busy === "start"} data-testid="interview-start">
            {busy === "start" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Start interview
          </Button>
        )}
      </div>

      {error && !locked && <p data-testid="interview-error" className="text-sm text-destructive">{error}</p>}

      {question && (
        <div className="border border-border" data-testid="interview-question-card">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <p className="overline text-muted-foreground">
              question {question.index + 1} / {question.total} · {question.category} · {question.difficulty}
            </p>
            {question.isFollowUp && <span className="overline text-primary">follow-up</span>}
          </div>
          <div className="p-5 space-y-4">
            <p className="font-display text-lg font-bold tracking-tight" data-testid="interview-question-text">{question.question}</p>
            <Textarea
              data-testid="interview-answer-input"
              rows={7}
              className="rounded-none text-sm"
              placeholder="Answer as you would out loud — structure, trade-offs, what you would check first…"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
            />
            <div className="flex gap-3">
              <Button className="rounded-none gap-2" onClick={submitAnswer} disabled={busy === "answer" || answer.trim().length < 10} data-testid="interview-submit-answer">
                {busy === "answer" && <Loader2 className="h-4 w-4 animate-spin" />} Submit answer
              </Button>
              {turns.length > 0 && (
                <Button variant="outline" className="rounded-none" onClick={finish} disabled={busy === "finish"} data-testid="interview-end-early">
                  End &amp; evaluate
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {turns.length > 0 && (
        <div className="space-y-px" data-testid="interview-transcript">
          <p className="overline text-muted-foreground">transcript</p>
          {turns.map((t, i) => (
            <div key={i} className="border border-border p-5" data-testid={`interview-turn-${i}`}>
              <div className="flex items-baseline justify-between gap-4">
                <p className="text-sm font-medium">{t.question}</p>
                <span className="font-mono text-sm shrink-0">{t.score}/100</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap">{t.answer}</p>
              <div className="mt-3"><ScoreBar value={t.score} /></div>
              <p className="mt-3 text-xs text-muted-foreground font-mono whitespace-pre-wrap">{t.feedback}</p>
            </div>
          ))}
        </div>
      )}

      {evaluation && (
        <div className="border border-border" data-testid="interview-evaluation">
          <div className="px-5 py-4 border-b border-border flex items-baseline justify-between">
            <p className="font-display font-bold tracking-tight flex items-center gap-2"><MessageSquareCode className="h-4 w-4 text-primary" /> Evaluation</p>
            <p className="font-mono text-lg" data-testid="interview-overall-score">{evaluation.overallScore}/100 · {evaluation.recommendation}</p>
          </div>
          <div className="p-5 space-y-4">
            <ScoreBar value={evaluation.overallScore} />
            <div className="border border-border bg-accent/40 p-4 text-xs whitespace-pre-wrap font-mono leading-relaxed">
              {evaluation.evaluation}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {evaluation.perQuestion.map((q, i) => (
                <div key={i} className="border border-border p-3">
                  <p className="font-mono text-xs text-muted-foreground">{q.category}</p>
                  <p className="mt-1 text-sm line-clamp-2">{q.question}</p>
                  <p className="mt-2 font-mono text-xs">{q.score}/100</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div>
        <h2 className="font-display text-xl font-bold tracking-tight">Past sessions</h2>
        <div className="mt-5 border border-border divide-y divide-border" data-testid="interview-history">
          {history.length ? history.map((h) => (
            <div key={h.interviewId} className="px-5 py-3 flex flex-wrap items-center justify-between gap-3 font-mono text-xs">
              <span>{new Date(h.createdAt).toLocaleString()}</span>
              <span className="text-muted-foreground">{h.answered}/{h.total} answered · {h.status}</span>
              <span>{h.overallScore != null ? `${h.overallScore}/100 · ${h.recommendation}` : "—"}</span>
            </div>
          )) : <p className="px-5 py-4 text-sm text-muted-foreground">No interviews yet.</p>}
        </div>
      </div>
    </div>
  );
}
